import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Table, Button, Badge, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { Monitor, Plus, Edit2, Trash2, CheckCircle, XCircle, Users } from 'lucide-react';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  getCashRegisterStations,
  createCashRegisterStation,
  updateCashRegisterStation,
  deleteCashRegisterStation
} from '../services/api';
import StationResponsiblesModal from './StationResponsiblesModal';
import AuthContext from '../context/AuthContext';

const CashRegisterStationsManager = () => {
  const { t } = useTranslation();
  const { business } = useContext(AuthContext);
  // Responsibles management is part of Cajas Fase 1 (tarea 005), gated by the plan feature.
  const cashStationsEnabled = !!business?.plan_features?.cash_register_stations;
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [showResponsiblesModal, setShowResponsiblesModal] = useState(false);
  const [selectedStationForResponsibles, setSelectedStationForResponsibles] = useState(null);

  const fetchStations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCashRegisterStations();
      setStations(res.data);
    } catch {
      toast.error(t('cashRegisterPage.stations.errorFetch', 'Error al cargar las estaciones'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchStations(); }, [fetchStations]);

  const openCreateModal = () => {
    setEditingStation(null);
    setFormData({ name: '', description: '' });
    setShowModal(true);
  };

  const openEditModal = (station) => {
    setEditingStation(station);
    setFormData({ name: station.name, description: station.description || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    setSubmitting(true);
    try {
      if (editingStation) {
        await updateCashRegisterStation(editingStation.id, formData);
        toast.success(t('cashRegisterPage.stations.updated', 'Estación actualizada'));
      } else {
        await createCashRegisterStation(formData);
        toast.success(t('cashRegisterPage.stations.created', 'Estación creada'));
      }
      setShowModal(false);
      fetchStations();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'STATION_LIMIT_REACHED') {
        toast.error(t('cashRegisterPage.stations.limitReached', 'Has alcanzado el límite de estaciones de tu plan'));
      } else {
        toast.error(err.response?.data?.message || t('cashRegisterPage.stations.errorSave', 'Error al guardar'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (station) => {
    try {
      await updateCashRegisterStation(station.id, { is_active: !station.is_active });
      toast.success(
        station.is_active
          ? t('cashRegisterPage.stations.deactivated', 'Estación desactivada')
          : t('cashRegisterPage.stations.activated', 'Estación activada')
      );
      fetchStations();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'STATION_IN_USE') {
        toast.error(t('cashRegisterPage.stations.inUse', 'No se puede desactivar: la estación tiene una sesión abierta'));
      } else {
        toast.error(err.response?.data?.message || t('cashRegisterPage.stations.errorUpdate', 'Error al actualizar'));
      }
    }
  };

  const handleDelete = async (station) => {
    const result = await Swal.fire({
      title: t('cashRegisterPage.stations.deleteTitle', '¿Eliminar estación?'),
      text: t('cashRegisterPage.stations.deleteConfirm', 'Esta acción no se puede deshacer.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: 'var(--ev-action-danger-bg)',
      cancelButtonColor: 'var(--ev-bg-subtle)',
      confirmButtonText: t('common.delete', 'Eliminar'),
      cancelButtonText: t('common.cancel', 'Cancelar'),
    });
    if (!result.isConfirmed) return;

    try {
      await deleteCashRegisterStation(station.id);
      toast.success(t('cashRegisterPage.stations.deleted', 'Estación eliminada'));
      fetchStations();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'STATION_HAS_HISTORY') {
        toast.error(t('cashRegisterPage.stations.hasHistory', 'Esta estación tiene historial y no puede eliminarse. Desactívala en su lugar.'));
      } else {
        toast.error(err.response?.data?.message || t('cashRegisterPage.stations.errorDelete', 'Error al eliminar'));
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0 fw-semibold d-flex align-items-center gap-2">
            <Monitor size={16} />
            {t('cashRegisterPage.stations.title', 'Estaciones de Caja')}
          </h6>
          <small className="text-muted">
            {t('cashRegisterPage.stations.subtitle', 'Gestiona los terminales físicos de tu negocio')}
          </small>
        </div>
        <Button size="sm" variant="primary" onClick={openCreateModal}>
          <Plus size={14} className="me-1" />
          {t('cashRegisterPage.stations.addStation', 'Agregar Estación')}
        </Button>
      </div>

      {stations.length === 0 ? (
        <Alert variant="info" className="py-2">
          {t('cashRegisterPage.stations.empty', 'No hay estaciones configuradas.')}
        </Alert>
      ) : (
        <Table hover responsive size="sm">
          <thead>
            <tr>
              <th>{t('cashRegisterPage.stations.stationName', 'Nombre')}</th>
              <th>{t('cashRegisterPage.stations.description', 'Descripción')}</th>
              <th>{t('cashRegisterPage.stations.status', 'Estado')}</th>
              <th>{t('cashRegisterPage.stations.inUseBy', 'En uso por')}</th>
              {cashStationsEnabled && <th>{t('cashRegisterPage.stations.responsibles', 'Responsables')}</th>}
              <th className="text-end">{t('common.actions', 'Acciones')}</th>
            </tr>
          </thead>
          <tbody>
            {stations.map(station => (
              <tr key={station.id}>
                <td className="fw-medium">{station.name}</td>
                <td className="text-muted small">{station.description || '—'}</td>
                <td>
                  <Badge bg={station.is_active ? 'success' : 'secondary'}>
                    {station.is_active
                      ? t('cashRegisterPage.stations.active', 'Activa')
                      : t('cashRegisterPage.stations.inactive', 'Inactiva')}
                  </Badge>
                </td>
                <td>
                  {station.current_user_name
                    ? <span className="text-primary small fw-medium">{station.current_user_name}</span>
                    : <span className="text-muted small">{t('cashRegisterPage.stations.available', 'Disponible')}</span>
                  }
                </td>
                {cashStationsEnabled && (
                  <td>
                    <span className="text-muted small">
                      {(station.responsibles || []).length > 0
                        ? (station.responsibles || []).map(r => r.user_name).join(', ')
                        : <em>{t('cashRegisterPage.stations.unassigned', 'Sin asignar')}</em>}
                    </span>
                  </td>
                )}
                <td className="text-end">
                  {cashStationsEnabled && (
                    <Button
                      size="sm"
                      variant="outline-info"
                      className="me-1 py-0 px-1"
                      title={t('cashRegisterPage.stations.manageResponsibles', 'Gestionar Responsables')}
                      onClick={() => { setSelectedStationForResponsibles(station); setShowResponsiblesModal(true); }}
                    >
                      <Users size={13} />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="me-1 py-0 px-1"
                    title={t('common.edit', 'Editar')}
                    onClick={() => openEditModal(station)}
                  >
                    <Edit2 size={13} />
                  </Button>
                  <Button
                    size="sm"
                    variant={station.is_active ? 'outline-warning' : 'outline-success'}
                    className="me-1 py-0 px-1"
                    title={station.is_active ? t('cashRegisterPage.stations.deactivate', 'Desactivar') : t('cashRegisterPage.stations.activate', 'Activar')}
                    onClick={() => handleToggleActive(station)}
                  >
                    {station.is_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="py-0 px-1"
                    title={t('common.delete', 'Eliminar')}
                    onClick={() => handleDelete(station)}
                  >
                    <Trash2 size={13} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Responsibles Modal */}
      <StationResponsiblesModal
        show={showResponsiblesModal}
        onHide={() => { setShowResponsiblesModal(false); setSelectedStationForResponsibles(null); fetchStations(); }}
        station={selectedStationForResponsibles}
      />

      {/* Create / Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {editingStation
              ? t('cashRegisterPage.stations.editStation', 'Editar Estación')
              : t('cashRegisterPage.stations.addStation', 'Agregar Estación')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3" controlId="stationName">
              <Form.Label>{t('cashRegisterPage.stations.stationName', 'Nombre')}</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder={t('cashRegisterPage.stations.namePlaceholder', 'Ej: Caja 1, Caja Central')}
                required
                autoFocus
              />
            </Form.Group>
            <Form.Group controlId="stationDescription">
              <Form.Label>{t('cashRegisterPage.stations.description', 'Descripción')} <span className="text-muted">(opcional)</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder={t('cashRegisterPage.stations.descriptionPlaceholder', 'Ej: Terminal de bodega')}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.name.trim()}
            >
              {submitting ? <Spinner as="span" animation="border" size="sm" /> : t('common.save', 'Guardar')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default CashRegisterStationsManager;
